<script setup lang="ts">
import { Handle, Position, useNode } from '@vue-flow/core';
import { Tabs, useLayoutStore } from '@/store/layout/layoutStore';
import { Notification, Room } from '@/models';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { computed } from 'vue';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import { useRoomStore } from '@/store/entities/roomStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { allRoomTypesList } from '@/enums/room';
import { useOrderStore } from '@/store/order/orderStore';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { useFormStore } from '@/store/form/formStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import GatewayStatusBadge from '@/components/LoRaWanRadioCheck/Badges/GatewayStatusBadge.vue';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { InspectionPointLabel } from '@/models/inspectionPoint/InspectionPoint';
import { impedimentIconClass } from '@/enums/generic';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  swapMode: {
    type: Boolean,
    default: false,
  },
});

const nodesData = useNode(props.data.id).node;

const room = computed(() => {
  if (!props.data.id) return null;
  return useEntitiesStore().getEntityById(props.data.id);
});

const label = computed(() => {
  return room.value.getLabel();
});

const roomNumber = computed(() => {
  return room.value?.number;
});

const devices = computed(() => {
  return useRoomStore().getDeviceAndPlantTagsOfRoom(room.value.id);
});
const notInstalledDevices = computed(() => {
  return [
    ...useInstallationPointStore().getFailedInstallationAttempt(room.value.id),
    ...useInspectionPointStore()
      .getInspectionPointsByRoomId(room.value.id)
      .filter((ip) => ip.failedInstallationAttempt),
  ];
});

const isRoomDeletable = computed(() => {
  if (!room.value) return false;
  return useRoomStore().isDeletable(room.value);
});

const editRoom = (options?: { scrollToBottom?: boolean }) => {
  useFormStore().editEntity(room.value as Room, '', options);
};

const deleteEntity = () => {
  useEntitiesStore().deleteEntity(room.value as Room, new Notification());
};

const unmountedDevices = computed(() => {
  return useInstallationPointStore().getUnmountedDevicesInRoom(room.value.id);
});

const onHasRadiatorChange = (roomId: string, hasRadiator: boolean) => {
  setHasRadiator(roomId, hasRadiator);
  useLayoutStore().openSideMenu();
  useLayoutStore().selectTab(Tabs.DEVICES);
};

const setHasRadiator = (roomId: string, hasRadiator: boolean) => {
  const room = useRoomStore().getRoomById(roomId);
  const updatedRoom = new Room();
  Object.assign(updatedRoom, room);
  updatedRoom.hasRadiator = hasRadiator;
  useEntitiesStore().saveEntity(updatedRoom);
};

const hasFunkcheckStatuses = computed(
  () => useDmgw6Store().hasFunkcheckStatuses
);

const roomFunkcheckStatus = computed(() =>
  useDmgw6Store().getRoomFunkcheckStatus(room.value.id)
);

const hasOutdatedDevices = computed(() => {
  return useDeviceStore()
    .getAllDevicesInRoom(room.value.id)
    .some((d) => d.customizedNumber === '42000');
});

const swapRooms = (node: Room, event: PointerEvent) => {
  event.stopPropagation();
  event.preventDefault();
  const room = useRoomStore().getRoomById(node.id);
  useRoomStore().swapRoom(room);
};

const isRoomSelected = computed(() => {
  return useLayoutStore().currentNode?.id === room.value.id;
});

const installationLocations = computed(() => {
  return useInspectionPointStore().getLocationsByRoomId(room.value.id);
});
</script>

<template>
  <div v-if="swapMode" class="absolute left-[-55px] top-[33px]">
    <span
      v-if="useRoomStore().getLeftSibling(room)"
      class="mdi mdi-swap-horizontal arrow-btn"
      data-testid="swapButton"
      @click="swapRooms(data, $event)"
    ></span>
  </div>
  <div v-if="isRoomSelected" class="bg absolute right-0 top-1">
    <OptionsDropdown
      class="self-center"
      direction-x="right"
      icon="mdi-dots-vertical"
      :disable-hover="true"
      :disable-selected="true"
      data-testid="roomOptionsDropdown"
    >
      <li
        v-if="!data.isAccessible"
        class="text-light-warning dark:text-dark-warning"
        @click="editRoom({ scrollToBottom: true })"
      >
        <em class="mdi mdi-lock mr-2" />Hinderungsgrund aktualisieren
      </li>
      <li data-testid="editRoomButton" @click="editRoom">
        <em class="mdi mdi-pencil mr-2" />Bearbeiten
      </li>
      <li
        :class="[
          {
            'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
              !isRoomDeletable,
          },
        ]"
        data-testid="deleteRoomButton"
        @click="isRoomDeletable && deleteEntity()"
      >
        <em class="mdi mdi-delete mr-2" />Löschen
      </li>
    </OptionsDropdown>
  </div>
  <div
    :class="{
      error:
        useRoomGroupStore().isRoomGroupFinishedClicked(
          useRoomGroupStore().activeRoomGroupId
        ) && !useRoomStore().isFinished(data.id),
      selected: isRoomSelected,
    }"
    :data-testId="
      'roomNode_' +
      allRoomTypesList.find((r) => r.value === data?.usageType)?.name +
      '_' +
      data.number
    "
    class="node-room"
    @click="onNodeClicked"
  >
    <span
      v-if="hasFunkcheckStatuses && roomFunkcheckStatus"
      class="absolute -top-2 -right-2 z-10 text-xl leading-none"
      :class="{
        'mdi mdi-check-circle text-green-500':
          roomFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.success,
        'mdi mdi-loading mdi-spin text-gray-400':
          roomFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.pending,
        'mdi mdi-check-circle text-yellow-400':
          roomFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.partial,
        'mdi mdi-alert-circle text-red-500':
          roomFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.error,
        'mdi mdi-bluetooth-off text-gray-400':
          roomFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.disconnected,
      }"
    />
    <div
      class="overflow-hidden text-ellipsis text-sm font-bold"
      :class="[
        {
          'max-w-[94%]': isRoomSelected,
        },
      ]"
    >
      <em
        v-if="!data.isAccessible"
        class="mdi mr-1"
        :class="impedimentIconClass(data.typeNotAccessible)"
      />
      {{ label }}
    </div>

    <!-- Optional room number — muted subtitle under the label -->
    <div
      v-if="roomNumber"
      :data-testId="'roomNumber_' + data.id"
      class="overflow-hidden text-ellipsis text-xs opacity-60"
    >
      {{ roomNumber }}
    </div>

    <div
      v-if="
        useInspectionPointStore().getCompletedGatewaysByRoomId(data.id).length >
          0 && !useOrderStore().isOnSiteInspection()
      "
    >
      <BaseChip
        text="Gateway"
        icon="mdi mdi-router-wireless mr-1 text-sm"
        data-testid="installationLocationTag"
        class="border-green-600 bg-green-50 dark:border-green-600 dark:bg-green-950"
      />
    </div>
    <div v-if="useOrderStore().isOnSiteInspection()">
      <GatewayStatusBadge
        v-for="(ip, index) in installationLocations"
        :key="ip.type + index"
        :status="ip.inspectionData?.radioCheckResult"
        :is-selected="!!ip.inspectionData?.isInstallationLocation"
        class="mb-2"
      />
    </div>
    <div
      v-if="
        useRoomGroupStore().isRoomGroupFinishedClicked(
          useRoomGroupStore().activeRoomGroupId
        ) &&
        !useRoomStore().isFinished(data.id) &&
        useRoomStore().getRoomById(data.id).hasRadiator === undefined
      "
      class="flex flex-col text-sm"
    >
      <span class="font-bold" data-testId="hasRadiatorCheck"
        >Heizkörper da?</span
      >
      <div>
        <button
          class="w-16 rounded-l-full border-2 border-primary-2 bg-primary-3 py-2 dark:bg-dark-2"
          data-testId="confirmButton"
          @click="() => onHasRadiatorChange(data.id, true)"
        >
          Ja
        </button>

        <button
          class="w-16 rounded-r-full border-2 border-l-0 border-primary-2 bg-primary-3 py-2 dark:bg-dark-2"
          data-testId="declineButton"
          @click="() => onHasRadiatorChange(data.id, false)"
        >
          Nein
        </button>
      </div>
    </div>
    <div v-if="devices.length > 0">
      <template v-for="device in devices">
        <BaseChip
          v-if="
            device.customStyle && device.label !== InspectionPointLabel.GATEWAY
          "
          :key="device.id"
          :text="device.label"
          :number="!device?.customStyle?.iconClass ? device.info : undefined"
          :icon="device?.customStyle?.iconClass"
          :data-testId="device?.customStyle?.testId"
          :custom-color="device?.customStyle?.backgroundColor"
          :border-color="device?.customStyle?.borderColor"
          :class="
            device?.customStyle?.borderColor +
            ' ' +
            device?.customStyle?.backgroundColor +
            ' ' +
            device.customStyle?.textColor
          "
        />
        <BaseChip
          v-if="
            !device.customStyle && device.label !== InspectionPointLabel.GATEWAY
          "
          :key="device.id"
          :number="device.info"
          :text="device.label"
          :custom-color="device.color"
          :icon="hasOutdatedDevices ? 'mdi-circle text-red-500' : null"
          class="mr-1"
        />
      </template>
    </div>
    <div
      v-if="notInstalledDevices.length > 0"
      class="flex justify-center gap-1"
    >
      <BaseChip
        border-color="#ea2020"
        custom-color="#a20101"
        icon="mdi-alert-outline"
        :number="notInstalledDevices.length"
      />
    </div>
    <div v-if="unmountedDevices.length > 0" class="flex justify-center gap-1">
      <BaseChip
        border-color="#ffa451"
        custom-color="#744215"
        :number="unmountedDevices.length"
        text="demontiert"
      />
    </div>
    <div
      v-if="useRoomStore().hasRadiator(data.id) === false"
      class="mt-auto flex self-end text-xs"
    >
      Kein Heizkörper <em class="mdi mdi-radiator-off ml-1"></em>
    </div>
    <div
      v-if="
        useRoomGroupStore().isRoomGroupFinishedClicked(
          useRoomGroupStore().activeRoomGroupId
        ) &&
        useRoomStore().hasRadiator(data.id) &&
        useRoomStore().getActiveAndFailedSrtDevices(data.id).length === 0 &&
        data.isAccessible
      "
      class="mt-auto flex self-start text-left text-xs font-bold"
    >
      Geräteinformationen erfassen
    </div>
    <Handle
      id="b"
      type="target"
      :position="Position.Bottom"
      style="opacity: 0; bottom: 4px"
    />
    <Handle
      v-for="(handle, index) in data.children"
      :id="handle.id"
      :key="handle.id + index"
      type="source"
      :position="Position.Top"
      :style="`left: ${
        nodesData.children[index].x -
        nodesData.position.x +
        nodesData.children[index].width / 2
      }px; top: 4px; opacity: 0`"
    />
  </div>
</template>
